import { ChevronDown, type LucideIcon } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { createElement, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { ROLES, ROLE_HOME_PATHS, rolesVisibleInVertical } from './roleConfig'
import {
  getRoleIcon,
  getRoleLabel,
  useVertical,
  type Vertical,
} from './useVertical'

/**
 * A role's icon, resolved for the active vertical.
 *
 * Declared here rather than assigned to a capitalised local inside the component below.
 * `const Icon = getRoleIcon(...)` followed by `<Icon />` is indistinguishable — to React
 * and to the linter — from *defining* a component during render, which would remount it
 * and reset its state on every render. `createElement` says plainly that this selects an
 * existing component rather than making one.
 */
function RoleGlyph({
  vertical,
  role,
  fallback,
  className,
}: {
  vertical: Vertical
  role: string
  fallback: LucideIcon
  className?: string
}) {
  return createElement(getRoleIcon(vertical, role, fallback), { className })
}

export function RoleSwitcherButton() {
  const { activeRole, roles, switchRole } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [lastPath, setLastPath] = useState(location.pathname)
  const { activeVertical, setVertical } = useVertical()

  // Close the menu when navigation changes the route. Adjusting state during
  // render (React's endorsed pattern over an effect) avoids a cascading
  // re-render and keeps hook order stable across the early return below.
  if (lastPath !== location.pathname) {
    setLastPath(location.pathname)
    if (open) setOpen(false)
  }

  // Fulfiller only when the active service is Moves.
  const visibleRoleKeys = new Set(rolesVisibleInVertical(activeVertical, roles))
  const availableRoles = ROLES.filter((r) => visibleRoleKeys.has(r.key))
  if (availableRoles.length === 0) return null
  // If sticky activeRole is fulfiller but we left Moves, label falls back to consumer.
  const displayRoleKey =
    activeRole && visibleRoleKeys.has(activeRole)
      ? activeRole
      : (availableRoles[0]?.key ?? null)
  const activeRoleConfig = ROLES.find((r) => r.key === displayRoleKey)

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 rounded-2xl border border-stone-200 bg-white px-2.5 py-2 text-[10px] font-medium text-stone-700 shadow-sm hover:bg-stone-50 sm:gap-2 sm:px-3 sm:text-sm"
        aria-label="Switch active role"
        aria-expanded={open}
      >
        {activeRoleConfig && (
          <RoleGlyph
            vertical={activeVertical}
            role={activeRoleConfig.key}
            fallback={activeRoleConfig.icon}
            className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4"
          />
        )}
        <span className="whitespace-nowrap">
          {activeRoleConfig
            ? getRoleLabel(activeVertical, activeRoleConfig.key, activeRoleConfig.label)
            : 'Select role'}
        </span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-56 rounded-2xl border border-stone-200 bg-white p-2 shadow-xl">
          {availableRoles.map((r) => (
            <button
              key={r.key}
              onClick={() => {
                if (r.key === 'fulfiller') {
                  setVertical('moves')
                }
                switchRole(r.key)
                setOpen(false)
                navigate(ROLE_HOME_PATHS[r.key] ?? '/notifications')
              }}
              className={`block w-full rounded-xl px-3 py-2 text-left text-sm hover:bg-stone-50 ${
                r.key === displayRoleKey
                  ? 'bg-brand-50 font-semibold text-brand-800'
                  : 'text-stone-700'
              }`}
              aria-current={r.key === displayRoleKey ? 'true' : undefined}
            >
              <span className="flex items-center gap-3">
                <RoleGlyph
                  vertical={r.key === 'fulfiller' ? 'moves' : activeVertical}
                  role={r.key}
                  fallback={r.icon}
                  className="h-4 w-4"
                />
                {getRoleLabel(
                  r.key === 'fulfiller' ? 'moves' : activeVertical,
                  r.key,
                  r.label,
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
